import { zones } from 'Helpers/dates'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  Checkbox,
  Collapsible,
  Flex,
  FlexItem,
  FormElement,
  INPUT_NUMBER_VARIANT_DANGER,
  InputNumber,
  RadioButton,
  RADIOBUTTON_LABEL_FW_STANDARD,
  Select,
  SPACING_24,
  SpacingVertical,
  Text
} from 'mailup-web-components'

import { arrDaysOfWeek, STORE_KEY_LIST } from 'Constants/index'
import { storeGet } from 'mailup-react-helpers'
import { campaignState } from 'Pages/Campaign/recoil/state/campaign'
import {
  checkFrequencyState,
  stepState
} from 'Pages/Campaign/recoil/state/page'

const RadioButtonChoose = () => {
  const intl = useIntl()

  const [{ CheckFrequencyType }, setCampaign] = useRecoilState(campaignState)
  const [{ d }, setFrequency] = useRecoilState(checkFrequencyState)

  const setStepState = useSetRecoilState(stepState)

  const handleChange = () => {
    setCampaign((prev) => ({ ...prev, CheckFrequencyType: 2, isDirty: true }))
  }

  const handleChangeNumber = (value) => {
    const parsedValue = isNaN(parseInt(value)) ? 0 : parseInt(value)
    setCampaign((prev) => ({
      ...prev,
      CheckNumberOfNewContents: parsedValue,
      isDirty: true
    }))
    setStepState((prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        sendingFrequency: {
          ...prev.sendingFrequency,
          numberNewContentIsDirty: true
        }
      }
    }))
  }

  const handleChangeFrequency = (newValue, mode) => {
    const value = parseInt(newValue)
    switch (mode) {
      case 'D':
        {
          const isAlreadyPresent = d.find((elem) => elem === value)

          let dArray
          if (isAlreadyPresent !== undefined) {
            dArray = d.filter((elem) => elem !== value)
          } else {
            dArray = d.concat(value)
          }
          const sortedArray = dArray.sort((a, b) => a - b)

          setFrequency((prev) => ({ ...prev, d: sortedArray }))
        }
        break
      case 'H':
        setFrequency((prev) => ({ ...prev, h: isNaN(value) ? 0 : value }))

        break
      case 'M':
        setFrequency((prev) => ({ ...prev, m: isNaN(value) ? 0 : value }))

        break

      default:
        break
    }

    setStepState((prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        sendingFrequency: {
          ...prev.sendingFrequency,
          checkFrequencyIsDirty: true
        }
      }
    }))
    setCampaign((prev) => ({ ...prev, isDirty: true }))
  }

  const handleChangeTimeZone = (value) => {
    if (value) {
      setCampaign((prev) => ({ ...prev, Timezone: value, isDirty: true }))
      setStepState((prev) => ({
        ...prev,
        step3: {
          ...prev.step3,
          sendingFrequency: {
            ...prev.sendingFrequency,
            timeZoneisDirty: true
          }
        }
      }))
    }
  }

  return (
    <>
      <RadioButton
        checked={CheckFrequencyType === 2}
        id='radio-button--choose'
        label={intl.formatMessage({
          id: 'Load.ContentsCampaignWizard.FrequencyDataOra'
        })}
        labelVariant={RADIOBUTTON_LABEL_FW_STANDARD}
        name='radio-button--choose'
        onChange={handleChange}
        value={2}
        data-mu-test='campaign__step-3__radio-button-choose'
      />
      {CheckFrequencyType === 2 && (
        <Collapsible collpased={CheckFrequencyType === 2}>
          <Flex margin='16px 0 0 26px' flexDirection='column'>
            <FlexItem>
              <Flex alignItems='baseline'>
                <SelectNewContents handleChangeNumber={handleChangeNumber} />
              </Flex>
            </FlexItem>
            <SpacingVertical variant={SPACING_24} />
            <FlexItem>
              <Flex alignItems='baseline' flexWrap='wrap' rowGap='8px'>
                <SelectDay handleChangeFrequency={handleChangeFrequency} />
              </Flex>
            </FlexItem>
            <SpacingVertical variant={SPACING_24} />
            <FlexItem>
              <Flex
                alignItems='center'
                flexWrap='wrap'
                rowGap='8px'
                columnGap='8px'
              >
                <SelectTime handleChangeFrequency={handleChangeFrequency} />
                <SelectTimeZone handleChangeTimeZone={handleChangeTimeZone} />
              </Flex>
            </FlexItem>
          </Flex>
        </Collapsible>
      )}
    </>
  )
}

export default RadioButtonChoose

const SelectNewContents = ({ handleChangeNumber }) => {
  const intl = useIntl()

  const { CheckNumberOfNewContents } = useRecoilValue(campaignState)
  const {
    step3: {
      sendingFrequency: { numberNewContentIsDirty }
    }
  } = useRecoilValue(stepState)

  return (
    <>
      <Text>
        {intl.formatMessage(
          {
            id: 'Load.ContentsCampaignWizard.NuoviArticoliParte1Almeno'
          },
          {
            strong: (chunks) => <strong>{chunks}</strong>
          }
        )}
      </Text>
      <FlexItem margin='0 8px'>
        <FormElement
          feedback={
            numberNewContentIsDirty && !CheckNumberOfNewContents
              ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
              : undefined
          }
          variant={
            numberNewContentIsDirty && !CheckNumberOfNewContents
              ? 'danger'
              : undefined
          }
        >
          <FlexItem width='80px'>
            <InputNumber
              min={1}
              max={1000}
              onChange={handleChangeNumber}
              value={CheckNumberOfNewContents}
              data-mu-test='campaign__step-3__input-number-CheckNumberOfNewContents'
              variant={
                !CheckNumberOfNewContents && numberNewContentIsDirty
                  ? INPUT_NUMBER_VARIANT_DANGER
                  : ''
              }
            />
          </FlexItem>
        </FormElement>
      </FlexItem>
      <Text>
        {intl.formatMessage({
          id: 'Load.ContentsCampaignWizard.NuoviArticoliParte2'
        })}
      </Text>
    </>
  )
}

const SelectDay = ({ handleChangeFrequency }) => {
  const intl = useIntl()
  const { d } = useRecoilValue(checkFrequencyState)

  return (
    <>
      <Text htmlElement='span'>
        {intl.formatMessage({
          id: 'Load.ContentsCampaignWizard.LabelSelectGiorno'
        })}
      </Text>

      {arrDaysOfWeek.map((day) => (
        <FlexItem key={'day-' + day.name} padding='0 16px' flexShrink={'0'}>
          <Checkbox
            label={day.name}
            onChange={(e) => handleChangeFrequency(e.target.value, 'D')}
            checked={d.includes(day.value)}
            value={day.value}
            data-mu-test={'campaign__step-3__checkbox-day-' + day.name}
          />
        </FlexItem>
      ))}
    </>
  )
}

const SelectTime = ({ handleChangeFrequency }) => {
  const intl = useIntl()
  const { h, m } = useRecoilValue(checkFrequencyState)
  const {
    step3: {
      sendingFrequency: { checkFrequencyIsDirty }
    }
  } = useRecoilValue(stepState)

  return (
    <>
      <FlexItem>
        <Text htmlElement='span'>
          {intl.formatMessage({
            id: 'Load.ContentsCampaignWizard.LabelSelectOra'
          })}
        </Text>
      </FlexItem>
      <FlexItem width='80px'>
        <InputNumber
          min={0}
          max={23}
          onChange={(value) => handleChangeFrequency(value, 'H')}
          value={h}
          data-mu-test='campaign__step-3__input-number-CheckFrequency'
          variant={
            (h < 0 || h > 23) && checkFrequencyIsDirty
              ? INPUT_NUMBER_VARIANT_DANGER
              : ''
          }
          formatter={(value) => value.toString().padStart(2, '0')}
        />
      </FlexItem>
      <FlexItem>:</FlexItem>
      <FlexItem width='80px'>
        <InputNumber
          min={0}
          max={59}
          step={5}
          onChange={(value) => handleChangeFrequency(value, 'M')}
          value={m}
          data-mu-test='campaign__step-3__input-number-CheckFrequency'
          variant={
            (m < 0 || m > 59) && checkFrequencyIsDirty
              ? INPUT_NUMBER_VARIANT_DANGER
              : ''
          }
          formatter={(value) => value.toString().padStart(2, '0')}
        />
      </FlexItem>
    </>
  )
}

const SelectTimeZone = ({ handleChangeTimeZone }) => {
  const intl = useIntl()

  const { Timezone } = useRecoilValue(campaignState)
  const { TimeZoneCode: tzcode } = storeGet(STORE_KEY_LIST)

  return (
    <>
      <FlexItem margin='0 0 0 16px'>
        <Text htmlElement='span'>
          {intl.formatMessage({
            id: 'Load.Schedulation.setScheduleLblFuso'
          })}
        </Text>
      </FlexItem>
      <FlexItem width='350px'>
        <Select
          value={Timezone || tzcode}
          onChange={handleChangeTimeZone}
          searchable
          placeholder={intl.formatMessage({
            id: 'Load.Task.TimeZonePlaceholder'
          })}
          options={Object.entries(zones).map(([code, label]) => ({
            value: code,
            label: `(${code.replace(/([^.]+)\.\d+$/, '$1')}) ${label}`
          }))}
          data-mu-test='campaign__step-3__select-time-zone'
        />
      </FlexItem>
    </>
  )
}

SelectNewContents.propTypes = {
  handleChangeNumber: PropTypes.func
}

SelectDay.propTypes = {
  handleChangeFrequency: PropTypes.func
}

SelectTime.propTypes = {
  handleChangeFrequency: PropTypes.func
}

SelectTimeZone.propTypes = {
  handleChangeTimeZone: PropTypes.func
}
